import { useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { toast } from 'sonner'
import {
  selectCharterConfig,
  selectCharterGateStatus,
  selectCharterRoundControlLoading,
  selectCharterRoundStatus,
  selectTeamStatuses,
} from '../../redux/selectors/charterSelectors'
import {
  charterRoundControl,
  fetchCharterConfig,
  fetchCharterGateStatus,
  fetchCharterStatus,
} from '../../redux/asyncThunks/charterThunks'
import type { CharterRoundAction } from '../../api/charter'

interface CharterRoundControlPanelProps {
  roomId: string
  /** Smaller heading/spacing for the room GameControlPanel. */
  compact?: boolean
}

const PHASE_LABEL: Record<string, string> = {
  phase1_prep: 'Phase 1 · Individual Prep',
  phase2_group: 'Phase 2 · Group',
  ratified: 'Ratified',
}

const STATUS_LABEL: Record<string, string> = {
  not_started: 'not started',
  in_progress: 'in progress',
  completed: 'completed',
}

export function CharterRoundControlPanel({
  roomId,
  compact = false,
}: CharterRoundControlPanelProps) {
  const dispatch = useAppDispatch()
  const config = useAppSelector(selectCharterConfig)
  const rawRoundStatus = useAppSelector(selectCharterRoundStatus)
  const loadingAction = useAppSelector(selectCharterRoundControlLoading)
  const gateStatus = useAppSelector(selectCharterGateStatus)
  const teamStatuses = useAppSelector(selectTeamStatuses)
  // A standalone Charter Class has no game, so the "Round 0 / pre-game / gate"
  // framing does not apply to it.
  const isCharterClass = useAppSelector(
    (state) => state.room.currentRoom?.is_charter_only === true
  )
  const [confirmResetOpen, setConfirmResetOpen] = useState(false)

  useEffect(() => {
    if (!roomId) return
    dispatch(fetchCharterConfig({ roomId }))
    dispatch(fetchCharterStatus({ roomId }))
    dispatch(fetchCharterGateStatus({ roomId }))
    // Poll so the professor's view reflects students self-driving the charter
    // (the professor isn't connected to the charter WebSocket).
    const id = setInterval(() => {
      dispatch(fetchCharterStatus({ roomId }))
      dispatch(fetchCharterGateStatus({ roomId }))
    }, 5000)
    return () => clearInterval(id)
  }, [dispatch, roomId])

  // Derive the *effective* round status from the actual per-team sessions, not
  // just config.round_status (which stays "not_started" when students self-drive
  // the charter over WebSocket). This is what makes Reset appear after teams
  // ratify instead of Start.
  const { effective, anyPhase1, anyPhase2, noTeams } = useMemo(() => {
    const sessions = teamStatuses
      .map((t) => t.session)
      .filter((s): s is NonNullable<typeof s> => Boolean(s))
    const allLocked =
      sessions.length > 0 && sessions.every((s) => s.status === 'locked')
    const anyActive = sessions.some((s) => s.status !== 'not_started')
    const eff: 'not_started' | 'in_progress' | 'completed' =
      allLocked || rawRoundStatus === 'completed'
        ? 'completed'
        : anyActive || rawRoundStatus === 'in_progress'
          ? 'in_progress'
          : 'not_started'
    return {
      effective: eff,
      anyPhase1: sessions.some((s) => s.phase === 'phase1_prep'),
      anyPhase2: sessions.some((s) => s.phase === 'phase2_group'),
      noTeams: teamStatuses.length === 0,
    }
  }, [teamStatuses, rawRoundStatus])

  if (!config?.enabled) return null

  const isBusy = loadingAction !== null
  const inProgress = effective === 'in_progress'

  const run = (action: CharterRoundAction) => {
    dispatch(charterRoundControl({ roomId, action }))
      .unwrap()
      .then(() => {
        const labels: Record<CharterRoundAction, string> = {
          start: 'Charter round started',
          advance: 'Advanced all teams to Phase 2',
          previous: 'Moved all teams back to Phase 1',
          end: 'Charter round ended',
          reset: 'Charter round reset to Phase 1',
        }
        toast.success(labels[action])
      })
      .catch((err) => toast.error(typeof err === 'string' ? err : 'Action failed'))
  }

  const btnClass = 'text-white bg-dark-blue hover:bg-dark-blue-hovered'

  return (
    <div className='space-y-3'>
      <div className='flex flex-wrap items-center justify-between gap-2'>
        <div className='flex items-center gap-2'>
          <h3
            className={
              compact
                ? 'text-sm font-medium text-dark-blue'
                : 'text-lg font-medium text-dark-blue'
            }
          >
            {isCharterClass ? 'Charter Session' : 'Round 0 — Pre-Game Charter'}
          </h3>
          <span className='rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600'>
            {/* With no teams only individual prep can be happening. */}
            {noTeams && effective === 'in_progress'
              ? 'prep open · no teams yet'
              : STATUS_LABEL[effective]}
          </span>
        </div>
        {/* The gate blocks game start, which a Charter Class does not have. */}
        {gateStatus?.gate_enabled && !isCharterClass && (
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-medium ${
              gateStatus.gate_satisfied
                ? 'bg-green-100 text-green-700'
                : 'bg-amber-100 text-amber-700'
            }`}
          >
            Gate: {gateStatus.teams_ratified ?? 0}/{gateStatus.teams_total ?? 0}{' '}
            ratified
          </span>
        )}
      </div>

      <TooltipProvider>
        <div className='flex flex-wrap gap-4'>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className={btnClass}
                onClick={() => run('start')}
                disabled={isBusy || effective !== 'not_started'}
              >
                {loadingAction === 'start' ? 'Starting...' : 'Start Round'}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                {isCharterClass
                  ? 'Begin Phase 1 (Individual Preparation) for all teams'
                  : 'Begin Round 0 — Phase 1 (Individual Preparation) for all teams'}
              </p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className={btnClass}
                onClick={() => run('advance')}
                disabled={isBusy || !inProgress || !anyPhase1}
              >
                {loadingAction === 'advance' ? 'Ending...' : 'End Phase'}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>End Phase 1 and move all teams to Phase 2 (Group Charter)</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className={btnClass}
                onClick={() => run('previous')}
                disabled={isBusy || !inProgress || !anyPhase2}
              >
                {loadingAction === 'previous' ? 'Rewinding...' : 'Previous Phase'}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Return all teams to Phase 1 (Individual Preparation)</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className={btnClass}
                onClick={() => run('end')}
                disabled={isBusy || !inProgress}
              >
                {loadingAction === 'end' ? 'Ending...' : 'End Round'}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                {isCharterClass
                  ? 'End the charter session. Ratified charters stay available to view and export.'
                  : 'End Round 0. The charter gate (if enabled) still governs game start.'}
              </p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant='destructive'
                onClick={() => setConfirmResetOpen(true)}
                disabled={isBusy || effective === 'not_started'}
              >
                {loadingAction === 'reset' ? 'Resetting...' : 'Reset Round'}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Wipe all charter progress and restart from a fresh Phase 1</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>

      {teamStatuses.length > 0 && (
        <div className='flex flex-wrap gap-2'>
          {teamStatuses.map((t) => (
            <span
              key={t.team_id}
              className='rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-600'
            >
              <span className='font-medium text-gray-800'>{t.team_name}</span>
              {': '}
              {t.session
                ? PHASE_LABEL[t.session.phase] ?? t.session.phase
                : 'not started'}
              {t.session?.status === 'locked' && ' ✓'}
            </span>
          ))}
        </div>
      )}

      <Dialog open={confirmResetOpen} onOpenChange={setConfirmResetOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset charter round?</DialogTitle>
            <DialogDescription>
              This permanently deletes all prep chats, group messages and
              consensus for every team, then restarts a fresh Phase 1
              (Individual Preparation). This cannot be undone.
              <br />
              <br />
              Charters that were already ratified are archived rather than
              destroyed, but teams start again from scratch.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setConfirmResetOpen(false)}
              disabled={loadingAction === 'reset'}
            >
              Cancel
            </Button>
            <Button
              variant='destructive'
              disabled={loadingAction === 'reset'}
              onClick={() => {
                run('reset')
                setConfirmResetOpen(false)
              }}
            >
              {loadingAction === 'reset' ? 'Resetting...' : 'Reset Round'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
